import { useMutation } from '@apollo/client'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Dialog, DialogContent, makeStyles, Theme } from '@material-ui/core'
import { CreatePPLFamilyFromListingMutation } from 'api/graphQL/mutations/createPPLFamilyFromListing.mutation'
import React from 'react'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import { AddProprietaryListingDetailInput, DisplayTextFormatType, PPLRequestType, SelectionType } from '../ProductListingDialog.types'
import AddToExistingFamily from './AddToExistingFamily'
import ClashDetectionModal from './ClashDetectionModal'
import CreateNewFamily from './CreateNewFamily'
import ManageUnmappedDialogActions from './DialogActions'
import ManageUnmappedDialogTitle from './DialogTitle'
import ManageUnmappedProductDialog from './ManageUnmappedProductDialog'


const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            width: pxToRem(500),
        },
        '.MuiDialogTitle-root': {
            paddingBottom: 0
        }
    },
    dialogContent: {
        padding: theme.spacing(0, 3, 2),
    },

}))

export type PPLFormData = {
    sectionName: string
    sectionNumber: string
    productTypeName: string
    selectedFamily: Record<string, string> | null
    selectedOption: 'existing' | 'new'
    existingProductType?: string
    newProductTypeName?: string
    contentItemId?: string
    elementId?: string
}

export type PPLFormErrors = {
    sectionName?: string
    sectionNumber?: string
    productTypeName?: string
    selectedFamily?: string
    selectedOption?: string
    existingProductType?: string
    newProductTypeName?: string
    contentItemId?: string
    elementId?: string
}

/**
 * Represents the different steps in the manage unmapped product workflow
 */
export type StepType =
    | 'initial'
    | 'createNewFamily'
    | 'addToExistingFamily'
    | 'clashDetectionNewFamily'
    | 'clashDetectionExistingFamily'

/**
 * Result data returned when the manage unmapped product workflow is completed
 */
export interface ManageUnmappedProductWizardResult {
    /** The type of operation performed */
    type: 'createNewFamily' | 'addToExistingFamily'
    /** The form data submitted by the user */
    data: Partial<PPLFormData>
}

/**
 * Props for the ManageUnmappedProductWizard component
 */
export interface ManageUnmappedProductWizardProps {
    open: boolean
    onClose: () => void
    /** Callback when the workflow is completed with result data */
    onComplete: (result: ManageUnmappedProductWizardResult) => void
    /** Callback when an error occurs during the workflow */
    onError?: (error: Error) => void
    /** Optional name of the product being managed */
    productName?: string
    /** The listing ID for the unmapped product */
    listingId?: string
    /** The project ID where the operation is being performed */
    projectId?: string,
    /** Function to handle adding the product option */
    handleAddProductOption: (
        listingId: string,
        selectionType: SelectionType,
        withManufacturerOption: boolean,
        withMasterSpecOption: boolean,
        displayTextFormat: DisplayTextFormatType,
        addProprietaryListingDetail?: AddProprietaryListingDetailInput
    ) => Promise<void>
}

interface StepState {
    currentStep: StepType
    formData: {
        initialChoice?: string
        createNewFamily?: Partial<PPLFormData>
        addToExistingFamily?: Partial<PPLFormData>
    }
    clashDetectionError?: string
}

const defaultFormData: PPLFormData = {
    selectedFamily: null,
    selectedOption: 'new',
    existingProductType: '',
    newProductTypeName: '',
    sectionName: '',
    sectionNumber: '',
    productTypeName: '',
}

/**
 * Main orchestrator component for managing unmapped products. This is AE only and won't be available in BPM side. 
 * 
 * This component provides a multi-step workflow that allows users to either:
 * 1. Create a new family for an unmapped product
 * 2. Add the product to an existing family
 * 
 * The workflow includes clash detection and form validation at each step.
 * 
 * @param props - The component props
 * @returns The ManageUnmappedProductWizard component
 */
const ManageUnmappedProductWizard: React.FC<ManageUnmappedProductWizardProps> = ({
    open,
    onClose,
    onComplete,
    onError,
    handleAddProductOption,
    productName,
    listingId,
    projectId,
}) => {
    const classes = useStyles()

    const [isFormValid, setIsFormValid] = React.useState(true)

    const [formData, setFormData] = React.useState<PPLFormData>(defaultFormData)

    const { showGlobalSnackbar } = useGlobalSnackbar()
    const [errors, setErrors] = React.useState<PPLFormErrors>({})

    const [selectedOption, setSelectedOption] = React.useState<string>('new')

    const [state, setState] = React.useState<StepState>({
        currentStep: 'initial',
        formData: {},
    })

    const [createPPLFamilyFromListing, { loading: createPPLLoading }] = useMutation(
        CreatePPLFamilyFromListingMutation,
        {
            errorPolicy: 'all',
        }
    )


    const handleInitialChoice = React.useCallback(() => {
        if (selectedOption === 'new') {
            setState(prev => ({
                ...prev,
                currentStep: 'createNewFamily',
                formData: {
                    ...prev.formData,
                    initialChoice: selectedOption,
                },
            }))
        } else {
            setState(prev => ({
                ...prev,
                currentStep: 'addToExistingFamily',
                formData: {
                    ...prev.formData,
                    initialChoice: selectedOption,
                },
            }))
        }
    }, [selectedOption])

    const handleClashDetectionComplete = React.useCallback(() => {
        const currentFormData = state.formData

        try {
            if (state.currentStep === 'clashDetectionNewFamily' && currentFormData.createNewFamily) {
                onComplete({
                    type: 'createNewFamily',
                    data: currentFormData.createNewFamily,
                })
            } else if (state.currentStep === 'clashDetectionExistingFamily' && currentFormData.addToExistingFamily) {
                onComplete({
                    type: 'addToExistingFamily',
                    data: currentFormData.addToExistingFamily,
                })
            }

            // Close the dialog and reset state
            setState({
                currentStep: 'initial',
                formData: {},
            })
            onClose()
        } catch (error) {
            console.error('[ManageUnmappedProductWizard] Error in handleClashDetectionComplete', error)
            onError?.(error as Error)
        }
    }, [state, onComplete, onClose, onError])

    const handleClashDetectionError = React.useCallback((errorMessage: string) => {
        setState(prev => {
            const newStep = prev.currentStep === 'clashDetectionNewFamily' ? 'createNewFamily' : 'addToExistingFamily'
            return {
                ...prev,
                currentStep: newStep,
                clashDetectionError: errorMessage,
            }
        })

        showGlobalSnackbar({
            variables: {
                message: errorMessage || 'An unknown error occured',
                variant: 'error',
                id: `ProductListingsModal_PPLAddToFamilyError`,
            },
        })
    }, [showGlobalSnackbar])

    const handleCreateNewFamilySave = React.useCallback(async () => {
        // Start clash detection for creating new family
        if (!listingId) {
            handleClashDetectionError('Missing listing ID. Please try again.')
            return
        }

        // Set state to clash detection step
        setState(prev => ({
            ...prev,
            currentStep: 'clashDetectionNewFamily',
            formData: {
                ...prev.formData,
                createNewFamily: formData,
            },
        }))

        try {
            // Await the promise from handleAddProductOption
            await handleAddProductOption(
                listingId,
                'Product',
                false,
                false,
                "ManufacturerAndProduct",
                {
                    addProprietaryListingOption: PPLRequestType.newFamily,
                    newFamilyNumber: formData.sectionNumber,
                    newProductFamilyName: formData.sectionName,
                    newProductTypeName: formData.productTypeName
                }
            )

            // Success: Show success snackbar and close dialog
            showGlobalSnackbar({
                variables: {
                    message: 'Product listing added successfully',
                    variant: 'success',
                    id: 'ProductListingsModal_PPLAddSuccess',
                },
            })

            // Close the dialog and reset state
            setState({
                currentStep: 'initial',
                formData: {},
            })
            onClose()

        } catch (error) {
            // Error: Show error snackbar and return to previous step
            const errorMessage = error instanceof Error ? error.message : 'Failed to add product listing. Please try again.'
            handleClashDetectionError(errorMessage)
        }

    }, [createPPLFamilyFromListing, listingId, formData, handleClashDetectionError, handleClashDetectionComplete, projectId, handleAddProductOption])

    const parseFamilyValue = React.useCallback((value: string) => {
        const parts = value.split(" - ")
        return {
            number: parts[0],
            name: parts.slice(1).join(" - ") // Handle names with " - "  in them
        }
    }, [])

    const handleAddToExistingFamilySave = React.useCallback(async () => {
        // Start clash detection for adding to existing family
        if (!listingId) {
            handleClashDetectionError('Missing listing ID. Please try again.')
            return
        }

        // Set state to clash detection step
        setState(prev => ({
            ...prev,
            currentStep: 'clashDetectionExistingFamily',
            formData: {
                ...prev.formData,
                addToExistingFamily: formData,
            },
        }))

        try {
            const { number, name } =
                parseFamilyValue(formData.selectedFamily?.value || '')

            // Determine product type name based on selected option
            const productTypeName = formData.selectedOption === 'existing'
                ? formData.existingProductType
                : formData.newProductTypeName

            // Await the promise from handleAddProductOption
            await handleAddProductOption(
                listingId,
                'Product',
                false,
                false,
                "None",
                {
                    addProprietaryListingOption: formData.selectedOption === 'existing' ? PPLRequestType.existingProductType : PPLRequestType.existingFamily,
                    newFamilyNumber: number,
                    newProductFamilyName: name,
                    newProductTypeName: productTypeName || ""
                }
            )

            // Success: Show success snackbar and close dialog
            showGlobalSnackbar({
                variables: {
                    message: 'Product listing added successfully',
                    variant: 'success',
                    id: 'ProductListingsModal_PPLAddSuccess',
                },
            })

            // Close the dialog and reset state
            setState({
                currentStep: 'initial',
                formData: {},
            })
            onClose()

        } catch (error) {
            // Error: Show error snackbar and return to previous step
            const errorMessage = error instanceof Error ? error.message : 'Failed to add product listing. Please try again.'
            handleClashDetectionError(errorMessage)
        }

    }, [createPPLFamilyFromListing, listingId, formData, parseFamilyValue, handleClashDetectionError, handleClashDetectionComplete, projectId, handleAddProductOption])

    const handleClose = React.useCallback(() => {
        setState({
            currentStep: 'initial',
            formData: {},
            clashDetectionError: undefined,
        })
        setSelectedOption('new')
        setFormData(defaultFormData)
        setErrors({})
        onClose?.()
    }, [onClose])

    const handleContinue = React.useMemo(() => ({
        initial: handleInitialChoice,
        createNewFamily: handleCreateNewFamilySave,
        addToExistingFamily: handleAddToExistingFamilySave,
        clashDetectionNewFamily: handleClashDetectionComplete,
        clashDetectionExistingFamily: handleClashDetectionComplete,
    }), [handleInitialChoice, handleCreateNewFamilySave, handleAddToExistingFamilySave, handleClashDetectionComplete, handleAddProductOption])

    const renderChildren = React.useMemo(() => ({
        initial: <ManageUnmappedProductDialog selectedOption={selectedOption} setSelectedOption={setSelectedOption} />,
        createNewFamily: <CreateNewFamily
            setIsFormValid={setIsFormValid} formData={formData} setFormData={setFormData} errors={errors} setErrors={setErrors} />,
        addToExistingFamily: <AddToExistingFamily setIsFormValid={setIsFormValid} formData={formData} setFormData={setFormData} errors={errors} setErrors={setErrors} />,
        clashDetectionNewFamily: <ClashDetectionModal
            subtitle='Create a New Family'
            progressText="Checking for Clashes"

        />,
        clashDetectionExistingFamily: <ClashDetectionModal
            subtitle='Add to Existing Family'

            progressText="Checking for Clashes"
        />,
    }), [selectedOption, setSelectedOption, formData, setFormData, errors, setErrors, setIsFormValid])

    // Restore form data when reverting from clash detection error
    React.useEffect(() => {
        if (state.currentStep === 'createNewFamily' && state.formData.createNewFamily) {
            setFormData(state.formData.createNewFamily as PPLFormData)
        } else if (state.currentStep === 'addToExistingFamily' && state.formData.addToExistingFamily) {
            setFormData(state.formData.addToExistingFamily as PPLFormData)
        }
    }, [state.currentStep, state.formData])

    React.useEffect(() => {
        if (!open) {
            setState({
                currentStep: 'initial',
                formData: {},
                clashDetectionError: undefined,
            })
            setSelectedOption('new')
            setFormData(defaultFormData)
            setErrors({})
            setIsFormValid(true)
        }
    }, [open])

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                className={classes.dialog}
                aria-labelledby="manage-unmapped-product-dialog-wizard"
            >
                <ManageUnmappedDialogTitle
                >
                    Manage Product Listing
                </ManageUnmappedDialogTitle>

                <DialogContent className={classes.dialogContent}>

                    {
                        renderChildren[state.currentStep]
                    }

                    {(state.currentStep !== "clashDetectionNewFamily" && state.currentStep !== "clashDetectionExistingFamily") && <ManageUnmappedDialogActions
                        onCancel={handleClose}
                        onContinue={handleContinue[state.currentStep]}
                        disabled={!isFormValid || createPPLLoading}
                    />}
                </DialogContent>
            </Dialog>
        </>
    )
}

export default ManageUnmappedProductWizard